import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cause, QueryParams, SuggestionStatus, YesOrNo } from '../types';
import { getCauses, getCausesByTemplateId } from '../utils/httpRequest';
import { RootState } from '../store';
import { linkCauseToTemplate } from './templates';

export interface CauseState {
  causes: Cause[];
  loading: boolean;
  error?: string;
}

const initialState: CauseState = {
  causes: [],
  loading: false,
};

export const fetchCauses = createAsyncThunk(
  'machines/fetchCauses',
  async ({
    templateID,
    params,
  }: {
    templateID?: string;
    params?: QueryParams;
  }) => {
    const causes = await (templateID
      ? getCausesByTemplateId(templateID, params)
      : getCauses(params));

    return causes;
  }
);

export const causesSlice = createSlice({
  name: 'causes',
  initialState,
  reducers: {
    setCauses: (state, action: PayloadAction<Cause[]>) => {
      state.causes = action.payload;
    },
    addCause: (state, action: PayloadAction<Cause>) => {
      state.causes = [action.payload, ...state.causes];
    },
    removeCause: (state, action: PayloadAction<string>) => {
      state.causes = state.causes.filter(
        ({ causeID }) => causeID !== action.payload
      );
    },
    updateCause: (state, action: PayloadAction<Cause>) => {
      const index = state.causes.findIndex(
        ({ causeID }) => causeID === action.payload.causeID
      );
      const nextCauses = [...state.causes];
      nextCauses[index] = action.payload;
      state.causes = [...nextCauses];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCauses.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCauses.fulfilled, (state, action) => {
        state.causes = action.payload.data;
        state.loading = false;
      })
      .addCase(fetchCauses.rejected, (state, action) => {
        state.error = 'Error';
        state.loading = false;
      });
  },
});

export const { setCauses, addCause, removeCause, updateCause } =
  causesSlice.actions;

export default causesSlice.reducer;

export const selectCauses = (state: RootState) => state.causes.causes;
export const selectIsFetchingCauses = (state: RootState) =>
  state.causes.loading;

export const selectCausesForMachine = (state: RootState) =>
  (state.causes.causes || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.NO ||
      i.suggestionStatus === SuggestionStatus.REVIEWED
  );
